import React from 'react';
import {Field} from 'formik';
import {MenuItem, TextField} from "@mui/material";
import * as MuiIcons from "@mui/icons-material";
import TypologieLavageSelector from "../../layout/forms/TypologieLavageSelector.js";
import {getPicto} from "../../../../utils/pictoCatalog.js";
import {
    ActeNumber, FieldLabel, Hairline, PhotoStepperCard, Stepper, SwitchCard, UI, cardBaseSx,
} from "./FormUiKit.js";

/**
 * Carte « Acte 1 · LAVAGE » (refonte handoff FormNewOrEdit v2).
 * Acte principal, verrouillé (ni reorder ni delete) : header plein navy.
 * Contient toute la config du lavage : typologie (variante 1b), photos
 * avant/après (steppers), lavage partiel (switch-card + fallback + photos).
 *
 * `strategieLavageImpossible` : OFF = 0, ON = 1 (mapping switch → int existant).
 */
function ActeLavageCard({values, errors, typologiesLavage, hovered, onHover}) {
    const pictoEntry = getPicto('Opacity');
    const PictoCmp = MuiIcons[pictoEntry?.mui] || MuiIcons.Opacity;
    const partiel = Number(values.strategieLavageImpossible) !== 0;

    return (
        <div
            onMouseEnter={() => onHover?.('lavage')}
            onMouseLeave={() => onHover?.(null)}
            style={cardBaseSx(hovered)}
        >
            {/* Header navy — acte principal */}
            <div style={{display: "flex", alignItems: "center", gap: 10, padding: "12px 20px", background: UI.navy, color: "#fff"}}>
                <ActeNumber n={1} principal/>
                <PictoCmp sx={{fontSize: 16, color: "#fff"}}/>
                <span style={{fontSize: 14, fontWeight: 700, letterSpacing: ".06em"}}>LAVAGE</span>
                <span style={{
                    fontSize: 11, fontWeight: 600, padding: "3px 10px", borderRadius: 999,
                    background: "rgba(255,255,255,.15)", border: "1px solid rgba(255,255,255,.3)",
                }}>Acte principal</span>
            </div>

            <div style={{padding: "20px 24px 24px"}}>
                {/* Typologie */}
                <FieldLabel required>Typologie de lavage</FieldLabel>
                <div style={{fontSize: 12, color: UI.textMuted, margin: "2px 0 10px"}}>
                    Définit les éléments à laver et le libellé affiché à l'opérateur.
                </div>
                <Field name="typologieLavage">
                    {({form}) => (
                        <TypologieLavageSelector
                            typologies={typologiesLavage}
                            value={values.typologieLavage}
                            onChange={(v) => form.setFieldValue("typologieLavage", v)}
                        />
                    )}
                </Field>
                {errors.typologieLavage && (
                    <div style={{color: UI.red, fontSize: 12, marginTop: 4}}>{String(errors.typologieLavage)}</div>
                )}

                <Hairline/>

                {/* Photos avant / après */}
                <div style={{display: "flex", gap: 16, flexWrap: "wrap"}}>
                    <Field name="minPhotoAvant">
                        {({form}) => (
                            <PhotoStepperCard
                                label="Photos avant"
                                value={values.minPhotoAvant ?? 1}
                                onChange={(v) => form.setFieldValue("minPhotoAvant", v)}
                                hintZero="Facultatives — l'opérateur peut ne rien prendre"
                                hintSome="Minimum {n} avant de pouvoir terminer"
                            />
                        )}
                    </Field>
                    <Field name="minPhotoApres">
                        {({form}) => (
                            <PhotoStepperCard
                                label="Photos après"
                                value={values.minPhotoApres ?? 1}
                                onChange={(v) => form.setFieldValue("minPhotoApres", v)}
                                hintZero="Facultatives — l'opérateur peut ne rien prendre"
                                hintSome="Minimum {n} avant de pouvoir terminer"
                            />
                        )}
                    </Field>
                </div>

                <Hairline/>

                {/* Lavage partiel */}
                <Field name="strategieLavageImpossible">
                    {({form}) => (
                        <SwitchCard
                            checked={partiel}
                            onToggle={(on) => {
                                form.setFieldValue("strategieLavageImpossible", on ? 1 : 0);
                                if (!on) form.setFieldValue("typologieLavageFallback", null);
                            }}
                            title="Activer lavage partiel"
                            subtitle="Proposé si le lavage est impossible (ex. conteneur non collecté)"
                        />
                    )}
                </Field>

                {partiel && (
                    <div style={{marginTop: 12, display: "flex", alignItems: "flex-start", gap: 16, padding: "0 2px", flexWrap: "wrap"}}>
                        <div style={{flex: 1, minWidth: 220}}>
                            <FieldLabel>Typologie du lavage partiel</FieldLabel>
                            <Field name="typologieLavageFallback">
                                {({form}) => (
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        value={values.typologieLavageFallback || ''}
                                        onChange={(e) => form.setFieldValue("typologieLavageFallback", e.target.value || null)}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {borderRadius: "10px"},
                                            "& fieldset": {borderColor: UI.fieldBorder},
                                        }}
                                    >
                                        {(typologiesLavage || []).map((t) => (
                                            <MenuItem key={t['@id']} value={t['@id']}>
                                                <span style={{display: "inline-flex", alignItems: "center", gap: 8}}>
                                                    <span style={{width: 9, height: 9, borderRadius: "50%", background: t.couleur || UI.navy}}/>
                                                    {t.libelle}
                                                </span>
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            </Field>
                        </div>
                        <div>
                            <FieldLabel>Photos justificatives</FieldLabel>
                            <Field name="minPhotoLavageImpossible">
                                {({form}) => (
                                    <Stepper
                                        value={values.minPhotoLavageImpossible ?? 0}
                                        onChange={(v) => form.setFieldValue("minPhotoLavageImpossible", v)}
                                    />
                                )}
                            </Field>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ActeLavageCard;
